// @flow strict

import { useRef } from 'react';
import type { ContainerDimensions, FitStrategy, Source } from '../types';
import type { ResponsiveImageProps } from '../ResponsiveImage';
import getSource from './getSource';

type Options = {|
  allowedStretch: $PropertyType<ResponsiveImageProps, 'allowedStretch'>,
  containerDimensions: ContainerDimensions | null,
  getImageBasedOnClientInfo: boolean,
  postponeGettingSource: boolean,
  sources: $PropertyType<ResponsiveImageProps, 'sources'>,
  fitStrategy: FitStrategy,
|};

type Cache = {|
  containerDimensions: ContainerDimensions | null,
  result: null | Source,
  sources: Source[] | null,
|};

/** If the previous call to this method resulted in a valid result, cache the container size and result. */
const useCachedGetSource = ({
  allowedStretch,
  containerDimensions,
  getImageBasedOnClientInfo,
  postponeGettingSource,
  sources,
  fitStrategy,
}: Options) => {
  const cacheRef = useRef<Cache>({ containerDimensions: null, result: null, sources: null });
  const { current: cache } = cacheRef;

  if (postponeGettingSource || containerDimensions === null) {
    return null;
  }

  // We do not have to call the method again unless container size is larger.
  const cachedClientWidth =
    (cache.containerDimensions && cache.containerDimensions.clientWidth) || -1;
  const cachedClientHeight =
    (cache.containerDimensions && cache.containerDimensions.clientHeight) || -1;
  const clientWidth = (containerDimensions && containerDimensions.clientWidth) || 0;
  const clientHeight = (containerDimensions && containerDimensions.clientHeight) || 0;

  // if the sources match, the cached containerDimensions are at least the requested size, return the cached result
  if (
    cache.sources === sources &&
    containerDimensions &&
    cachedClientWidth >= clientWidth &&
    cachedClientHeight >= clientHeight &&
    cache.result
  ) {
    return cache.result;
  }

  // store a new value for the cache
  cacheRef.current = {
    result:
      containerDimensions !== null
        ? getSource(containerDimensions.clientWidth, containerDimensions.clientHeight, sources, {
            allowedStretch,
            getImageBasedOnClientInfo,
            fitStrategy,
          })
        : null,
    sources,
    containerDimensions,
  };

  // return the new result
  return cacheRef.current.result;
};

export default useCachedGetSource;
